import {Directive, ElementRef, forwardRef, HostListener, OnDestroy, OnInit} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR, NgControl} from '@angular/forms';
import {Subscription} from 'rxjs';

@Directive({
  selector: '[Phonenumber]',
  standalone: true, // Wenn du noch mit NgModules arbeitest, entferne 'standalone: true',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => Phonenumber),
      multi: true
    }
  ]
})
export class Phonenumber implements  ControlValueAccessor {
private readonly displayPrefix = '+(49) ';
private readonly modelPrefix = '+49';

private onChange: (value: string) => void = () => {};
private onTouched: () => void = () => {};

  constructor(private el: ElementRef<HTMLInputElement>) {}

  // --- ControlValueAccessor Methoden ---

  writeValue(value: string | null): void {
    const formatted = this.getFormattedValues(value || '');
    this.el.nativeElement.value = formatted.displayValue;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.el.nativeElement.disabled = isDisabled;
  }

  // --- Event Listener ---

@HostListener('input', ['$event'])
  onInput(event: Event) {
    const input = this.el.nativeElement;
    const rawValue = input.value;
    const rawCursorPos = input.selectionStart || 0;

    // 1. Zählen, wie viele Ziffern sich VOR dem Cursor befanden
    const textBeforeCursor = rawValue.substring(0, rawCursorPos);
    let digitsBeforeCursor = textBeforeCursor.replace(/\D/g, '');

    if (digitsBeforeCursor.startsWith('0049')) {
      digitsBeforeCursor = digitsBeforeCursor.substring(4);
    } else if (digitsBeforeCursor.startsWith('49')) {
      digitsBeforeCursor = digitsBeforeCursor.substring(2);
    }
    if (digitsBeforeCursor.startsWith('0')) {
      digitsBeforeCursor = digitsBeforeCursor.substring(1);
    }

    const userDigitsBeforeCursor = digitsBeforeCursor.length;

    // 2. Werte formatieren
    const { displayValue, modelValue } = this.getFormattedValues(rawValue);

    // 3. DOM-Wert aktualisieren
    input.value = displayValue;

    // 4. Cursor exakt hinter der letzten getippten Ziffer platzieren
    let newCursorPos = this.displayPrefix.length;
    let currentDigitCount = 0;

    if (userDigitsBeforeCursor > 0) {
      for (let i = this.displayPrefix.length; i < displayValue.length; i++) {
        if (/\d/.test(displayValue[i])) {
          currentDigitCount++;
          if (currentDigitCount === userDigitsBeforeCursor) {
            newCursorPos = i + 1;
            break;
          }
        }
      }
      if (userDigitsBeforeCursor > currentDigitCount) {
        newCursorPos = displayValue.length;
      }
    }

    if (newCursorPos < this.displayPrefix.length) {
      newCursorPos = this.displayPrefix.length;
    }

    input.setSelectionRange(newCursorPos, newCursorPos);

    // 5. Angular über Äußerung informieren
    this.onChange(modelValue);
  }

@HostListener('blur')
  onBlur() {
    this.onTouched();
  }

@HostListener('click')
@HostListener('keyup')
  onCursorMove() {
    const input = this.el.nativeElement;
    const currentPos = input.selectionStart || 0;

    if (currentPos < this.displayPrefix.length) {
      input.setSelectionRange(this.displayPrefix.length, this.displayPrefix.length);
    }
  }

@HostListener('focus')
  onFocus() {
    const input = this.el.nativeElement;

    setTimeout(() => {
      if (!input.value || !input.value.startsWith(this.displayPrefix)) {
        const { displayValue } = this.getFormattedValues(input.value);
        input.value = displayValue;
      }

      const currentPos = input.selectionStart || 0;
      if (currentPos < this.displayPrefix.length) {
        input.setSelectionRange(this.displayPrefix.length, this.displayPrefix.length);
      }
    });
  }

  // --- Hilfsmethode ---

private getFormattedValues(value: string): { displayValue: string; modelValue: string } {
    if (!value) value = '';

    let numbers = value.replace(/\D/g, '');

    if (numbers.startsWith('0049')) {
      numbers = numbers.substring(4);
    } else if (numbers.startsWith('49')) {
      numbers = numbers.substring(2);
    }

    if (numbers.startsWith('0')) {
      numbers = numbers.substring(1);
    }

    let displayValue = this.displayPrefix;
    let modelValue = this.modelPrefix;

    if (numbers.length > 0) {
      displayValue += numbers;
      modelValue += numbers;
    }

    return { displayValue, modelValue };
  }
}
